import { anfrage, KonfliktFehler, ladeHoch } from "./basis.ts";
import type {
  Sammelsendung,
  SammelsendungDokument,
  SammelsendungKindDokumente,
  SammelsendungMitJtlErgebnis,
  SammelsendungTask,
} from "../lib/types.ts";

/**
 * Schreibbare Kopfwerte. Ein fehlender Schluessel bleibt unangetastet, ein explizites `null`
 * leert den Wert - dieselbe Semantik wie im Backend (`schreibeGemeinsameWerte`).
 */
export type GemeinsameWerte = Partial<Pick<Sammelsendung, "invoiceNo" | "boardStatus" | "paymentTotalOverride" | "listDueDate">> & {
  /**
   * Zahlungsziel nur ueber die freigegebene Vorlage (Spec 29.09.): das Backend wendet sie je Kind
   * inkl. Raten-Neuaufbau an (Recht `manage_payments` zusaetzlich). Lieferant und Lager sind
   * reine Anzeige und werden nie geschrieben.
   */
  paymentTermsTemplateId?: string;
};

export interface SammelsendungsTaskEingabe {
  title: string;
  due?: string | null;
  sortIndex?: number;
  responsibilityId?: string | null;
  assignee?: string | null;
  deputyAssignee?: string | null;
}

export type SammelsendungsTaskAenderung = Partial<
  Pick<SammelsendungTask, "title" | "due" | "done" | "sortIndex" | "responsibilityId" | "assignee" | "deputyAssignee">
>;

const basis = (shipmentId: string) => `/api/shipments/${encodeURIComponent(shipmentId)}`;

export function sammelsendungLaden(shipmentId: string, signal?: AbortSignal): Promise<Sammelsendung> {
  return anfrage<Sammelsendung>(`${basis(shipmentId)}/parent`, { signal });
}

/** Liefert den aktuellen Parent und je Kind das Ergebnis der JTL-Durchschreibung (leer ohne Rechnungsnummer). */
export function gemeinsameWerteSchreiben(
  shipmentId: string,
  rowVersion: number,
  werte: GemeinsameWerte,
): Promise<SammelsendungMitJtlErgebnis> {
  return anfrage<SammelsendungMitJtlErgebnis>(`${basis(shipmentId)}/shared-values`, {
    methode: "PATCH",
    koerper: { rowVersion, ...werte },
  });
}

export function sammelsendungsTaskAnlegen(
  shipmentId: string,
  eingabe: SammelsendungsTaskEingabe,
): Promise<SammelsendungTask> {
  return anfrage<SammelsendungTask>(`${basis(shipmentId)}/parent-tasks`, {
    methode: "POST",
    koerper: eingabe,
  });
}

export function sammelsendungsTaskAendern(
  shipmentId: string,
  taskId: string,
  rowVersion: number,
  aenderung: SammelsendungsTaskAenderung,
): Promise<SammelsendungTask> {
  return anfrage<SammelsendungTask>(`${basis(shipmentId)}/parent-tasks/${encodeURIComponent(taskId)}`, {
    methode: "PATCH",
    koerper: { rowVersion, ...aenderung },
  });
}

/** `rowVersion` ist bei der ersten zentralen Ablage eines Dokumenttyps `null`. */
export function sammelsendungsDokumentVerknuepfen(
  shipmentId: string,
  docType: string,
  documentId: string,
  rowVersion: number | null,
): Promise<SammelsendungDokument> {
  return anfrage<SammelsendungDokument>(`${basis(shipmentId)}/parent-documents/${encodeURIComponent(docType)}`, {
    methode: "PUT",
    koerper: { documentId, rowVersion },
  });
}

/** Auswahl fuer das zentrale Dokument: die Dokumente der aktuellen Kinder, je Dokumenttyp. */
export async function kindDokumenteLaden(
  shipmentId: string,
  signal?: AbortSignal,
): Promise<SammelsendungKindDokumente[]> {
  const antwort = await anfrage<{ docTypes: SammelsendungKindDokumente[] }>(
    `${basis(shipmentId)}/member-documents`,
    { signal },
  );
  return antwort.docTypes;
}

/**
 * Neue Datei direkt zentral hochladen (multipart, der Server hasht und erkennt vorhandene Dateien selbst).
 * `rowVersion` ist bei der ersten zentralen Ablage eines Dokumenttyps `null` (Feld entfaellt).
 * `fortschritt` meldet den Sendeanteil 0 bis 1 (`basis.ts#ladeHoch`).
 */
export function sammelsendungsDokumentHochladen(
  shipmentId: string,
  docType: string,
  datei: File,
  rowVersion: number | null,
  fortschritt?: (anteil: number) => void,
): Promise<SammelsendungDokument> {
  const formular = new FormData();
  formular.append("file", datei);
  if (rowVersion !== null) formular.append("rowVersion", String(rowVersion));
  return ladeHoch<SammelsendungDokument>(
    `${basis(shipmentId)}/parent-documents/${encodeURIComponent(docType)}/upload`,
    formular,
    { onFortschritt: fortschritt },
  );
}

/**
 * Unterscheidet einen Versionskonflikt einer Parent-Route von jedem anderen Fehler und gibt den
 * mitgelieferten aktuellen Parent typisiert zurueck (`null`: kein Konflikt oder kein Parent im 409).
 * Der Backend-Konflikt traegt in `current` immer den vollstaendigen Parent.
 */
export function aktuelleSammelsendungAusKonflikt(fehler: unknown): Sammelsendung | null {
  if (!(fehler instanceof KonfliktFehler)) return null;
  const stand = fehler.aktuellerStand;
  if (typeof stand !== "object" || stand === null) return null;
  const kandidat = stand as Partial<Sammelsendung>;
  if (typeof kandidat.shipmentId !== "string" || !Array.isArray(kandidat.memberOrderIds)) return null;
  return stand as Sammelsendung;
}

/**
 * 409 der Route `POST /:shipmentId/aufloesen`, wenn sich die Mitgliederliste seit der Rueckfrage
 * geaendert hat: `memberOrderIds` sind die aktuellen Mitglieder. Ein 409 OHNE diese Liste (leere
 * Sammelsendung, gescheitertes Mitglied) bleibt ein gewoehnlicher `KonfliktFehler`.
 */
export class SammelsendungGeaendertFehler extends Error {
  readonly memberOrderIds: string[];

  constructor(memberOrderIds: string[], meldung: string) {
    super(meldung);
    this.name = "SammelsendungGeaendertFehler";
    this.memberOrderIds = memberOrderIds;
  }
}

/**
 * Loest die Sammelsendung auf: alle Mitglieder werden getrennt, die DSV-Sendung bleibt stehen.
 * `erwarteteOrderIds` ist die Liste, die der Nutzer in der Rueckfrage gesehen hat.
 */
export async function sammelsendungAufloesen(
  shipmentId: string,
  erwarteteOrderIds: readonly string[],
): Promise<{ aufgeloest: true; orderIds: string[] }> {
  try {
    return await anfrage<{ aufgeloest: true; orderIds: string[] }>(`${basis(shipmentId)}/aufloesen`, {
      methode: "POST",
      koerper: { erwarteteOrderIds },
    });
  } catch (fehler) {
    if (fehler instanceof KonfliktFehler) {
      const koerper = fehler.koerper;
      const liste = typeof koerper === "object" && koerper !== null ? (koerper as { memberOrderIds?: unknown }).memberOrderIds : undefined;
      if (Array.isArray(liste) && liste.every((x) => typeof x === "string")) {
        throw new SammelsendungGeaendertFehler(liste as string[], fehler.message);
      }
    }
    throw fehler;
  }
}
